import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoomDirectoryEntry, RoomType, RoomVisibility } from '@2d-space/contracts';
import { useNavigate, useSearchParams } from 'react-router';

import {
  createRoom,
  type CreateRoomFailure,
  type CreateRoomOutcome,
} from '../network/create-room.js';
import { enterPrivateRoom, enterPublicRoom, listRooms } from '../network/room-access.js';
import { writeRoomAuthority } from '../network/room-authority-storage.js';

const serverUrl = import.meta.env.VITE_SERVER_URL;
type CreatedRoom = Extract<CreateRoomOutcome, { ok: true }>;

export function WelcomeRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedRoom = searchParams.get('room') ?? '';
  const [activeTab, setActiveTab] = useState<RoomVisibility>(
    searchParams.get('visibility') === 'private' ? 'private' : 'public',
  );
  const [search, setSearch] = useState(linkedRoom);
  const [rooms, setRooms] = useState<readonly RoomDirectoryEntry[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [selectedPrivateSlug, setSelectedPrivateSlug] = useState(linkedRoom);
  const [privateCode, setPrivateCode] = useState('');
  const [title, setTitle] = useState('');
  const [roomType, setRoomType] = useState<RoomType>('instant');
  const [visibility, setVisibility] = useState<RoomVisibility>('public');
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState<CreateRoomFailure>();
  const [message, setMessage] = useState<string>();
  const directoryRequest = useRef(0);

  const refreshRooms = useCallback(
    async (query: string): Promise<void> => {
      const requestId = ++directoryRequest.current;
      setLoadingRooms(true);
      try {
        const nextRooms = await listRooms(serverUrl, activeTab, query);
        if (requestId !== directoryRequest.current) return;
        setRooms(nextRooms);
        setMessage(undefined);
      } catch {
        if (requestId !== directoryRequest.current) return;
        setMessage('The room directory is unavailable. Please try again.');
      } finally {
        if (requestId === directoryRequest.current) setLoadingRooms(false);
      }
    },
    [activeTab],
  );

  useEffect(() => {
    const timer = globalThis.setTimeout(() => void refreshRooms(search), 250);
    return () => globalThis.clearTimeout(timer);
  }, [refreshRooms, search]);

  const onCreateRoom = useCallback(async (): Promise<void> => {
    if (creating) return;
    setCreating(true);
    setFailure(undefined);
    setMessage(undefined);
    const outcome = await createRoom(serverUrl, { title, roomType, visibility });
    setCreating(false);
    if (!outcome.ok) {
      setFailure(outcome.failure);
      return;
    }
    try {
      saveCreatedAuthority(outcome);
    } catch {
      setMessage('This browser must allow local storage before it can own a room.');
      return;
    }
    await navigate(outcome.path);
  }, [creating, navigate, roomType, title, visibility]);

  async function openPublic(slug: string): Promise<void> {
    try {
      setMessage('Requesting access…');
      await navigate(await enterPublicRoom(serverUrl, slug));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The room could not be opened.');
    }
  }

  async function openPrivate(slug: string): Promise<void> {
    try {
      setMessage('Checking password…');
      await navigate(await enterPrivateRoom(serverUrl, slug, privateCode));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The room could not be opened.');
    }
  }

  function switchTab(tab: RoomVisibility): void {
    directoryRequest.current += 1;
    setActiveTab(tab);
    setRooms([]);
    setSelectedPrivateSlug('');
    setPrivateCode('');
    setMessage(undefined);
  }

  return (
    <main className="min-h-screen bg-[oklch(14%_0.015_155)] px-5 py-8 text-[oklch(94%_0.01_85)] sm:px-8 sm:py-10">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-col gap-5 border-b border-[oklch(30%_0.025_155)] pb-7 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.2em] text-[oklch(78%_0.13_145)]">
              2D Space
            </p>
            <h1 className="mt-2 text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">
              Find your room
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-[oklch(76%_0.02_145)]">
              Join an open room, unlock a private room, or create a space for your group.
            </p>
          </div>
          <span className="w-fit text-sm text-[oklch(66%_0.025_145)]">No account required</span>
        </header>

        {message !== undefined && (
          <p
            className="mt-5 rounded-lg border border-[oklch(47%_0.06_70)] bg-[oklch(21%_0.025_70)] px-4 py-3 text-sm"
            role="status"
          >
            {message}
          </p>
        )}

        <div className="mt-7 grid gap-8 lg:grid-cols-[minmax(0,1fr)_21rem]">
          <section aria-label="Room directory">
            <div
              className="flex border-b border-[oklch(31%_0.025_155)]"
              role="tablist"
              aria-label="Room visibility"
            >
              {(['public', 'private'] as const).map((tab) => (
                <button
                  aria-selected={activeTab === tab}
                  className={`relative min-w-28 px-4 py-3 text-sm font-semibold capitalize transition-colors duration-200 ${activeTab === tab ? 'text-[oklch(88%_0.08_145)] after:absolute after:inset-x-0 after:bottom-[-1px] after:h-0.5 after:bg-[oklch(75%_0.14_145)]' : 'text-[oklch(64%_0.02_145)] hover:text-[oklch(82%_0.03_145)]'}`}
                  key={tab}
                  role="tab"
                  type="button"
                  onClick={() => switchTab(tab)}
                >
                  {tab} rooms
                </button>
              ))}
            </div>

            <div className="mt-5 flex gap-3">
              <label className="min-w-0 flex-1">
                <span className="sr-only">Search {activeTab} rooms</span>
                <input
                  className="w-full rounded-lg border border-[oklch(37%_0.025_155)] bg-[oklch(17%_0.017_155)] px-4 py-3 text-sm outline-none transition-colors focus:border-[oklch(65%_0.1_145)]"
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Search ${activeTab} rooms`}
                  type="search"
                  value={search}
                />
              </label>
              <button
                className="rounded-lg border border-[oklch(39%_0.035_145)] px-4 text-sm font-semibold hover:bg-[oklch(21%_0.025_145)]"
                type="button"
                onClick={() => void refreshRooms(search)}
              >
                Refresh
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {loadingRooms && <RoomSkeleton />}
              {!loadingRooms && rooms.length === 0 && (
                <div className="rounded-xl border border-dashed border-[oklch(36%_0.025_155)] px-6 py-12 text-center">
                  <p className="font-semibold">No {activeTab} rooms found</p>
                  <p className="mt-1 text-sm text-[oklch(66%_0.02_145)]">
                    {search.trim() === ''
                      ? 'Create one to get started.'
                      : 'Try a different search.'}
                  </p>
                </div>
              )}
              {rooms.map((room) => (
                <RoomRow
                  code={selectedPrivateSlug === room.slug ? privateCode : ''}
                  key={room.slug}
                  room={room}
                  selected={selectedPrivateSlug === room.slug}
                  onCodeChange={setPrivateCode}
                  onJoin={() => {
                    if (room.visibility === 'public') void openPublic(room.slug);
                    else if (selectedPrivateSlug === room.slug) void openPrivate(room.slug);
                    else {
                      setSelectedPrivateSlug(room.slug);
                      setPrivateCode('');
                    }
                  }}
                />
              ))}
            </div>
          </section>

          <CreateRoomPanel
            creating={creating}
            failure={failure}
            roomType={roomType}
            title={title}
            visibility={visibility}
            onCreate={() => void onCreateRoom()}
            onRoomTypeChange={setRoomType}
            onTitleChange={setTitle}
            onVisibilityChange={setVisibility}
          />
        </div>
      </div>
    </main>
  );
}

function saveCreatedAuthority(created: CreatedRoom): void {
  writeRoomAuthority(globalThis.localStorage, serverUrl, created.slug, {
    browserCredential: created.browserCredential,
    roomCapability: created.ownerCapability,
    ...(created.privateAccessCode === undefined
      ? {}
      : { privateAccessCode: created.privateAccessCode }),
  });
}

function RoomRow({
  code,
  onCodeChange,
  onJoin,
  room,
  selected,
}: {
  code: string;
  onCodeChange: (value: string) => void;
  onJoin: () => void;
  room: RoomDirectoryEntry;
  selected: boolean;
}): React.JSX.Element {
  const full = room.occupancy >= room.capacity;
  return (
    <article className="rounded-xl border border-[oklch(32%_0.025_155)] bg-[oklch(18%_0.018_155)] p-4 transition-colors hover:border-[oklch(41%_0.045_145)]">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold">{room.title}</h2>
            {room.visibility === 'private' && <span aria-label="Private room">🔒</span>}
          </div>
          <p className="mt-1 text-sm text-[oklch(67%_0.025_145)]">
            {room.occupancy}/{room.capacity} people · {room.roomType}
            {room.approvalRequired ? ' · approval required' : ''}
          </p>
        </div>
        <button
          className="shrink-0 rounded-lg bg-[oklch(76%_0.14_145)] px-4 py-2 text-sm font-semibold text-[oklch(17%_0.025_150)] disabled:opacity-40"
          disabled={full || (selected && code.length !== 6)}
          type="button"
          onClick={onJoin}
        >
          {full
            ? 'Full'
            : room.visibility === 'private'
              ? selected
                ? 'Enter room'
                : 'Unlock'
              : 'Join'}
        </button>
      </div>
      {selected && room.visibility === 'private' && (
        <label className="mt-4 block border-t border-[oklch(30%_0.02_155)] pt-4 text-sm font-medium">
          Six-letter room password
          <input
            autoFocus
            className="mt-2 w-full rounded-lg border border-[oklch(42%_0.04_145)] bg-[oklch(14%_0.015_155)] px-4 py-3 font-mono text-lg uppercase tracking-[0.3em] outline-none focus:border-[oklch(66%_0.12_145)]"
            maxLength={6}
            onChange={(event) =>
              onCodeChange(event.target.value.replace(/[^a-z]/gi, '').toUpperCase())
            }
            placeholder="ABCDEF"
            value={code}
          />
        </label>
      )}
    </article>
  );
}

function RoomSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-3" aria-label="Loading rooms" role="status">
      {[0, 1, 2].map((item) => (
        <div className="h-20 animate-pulse rounded-xl bg-[oklch(19%_0.018_155)]" key={item} />
      ))}
    </div>
  );
}

function CreateRoomPanel({
  creating,
  failure,
  onCreate,
  onRoomTypeChange,
  onTitleChange,
  onVisibilityChange,
  roomType,
  title,
  visibility,
}: {
  creating: boolean;
  failure: CreateRoomFailure | undefined;
  onCreate: () => void;
  onRoomTypeChange: (value: RoomType) => void;
  onTitleChange: (value: string) => void;
  onVisibilityChange: (value: RoomVisibility) => void;
  roomType: RoomType;
  title: string;
  visibility: RoomVisibility;
}): React.JSX.Element {
  return (
    <section
      className="h-fit rounded-2xl border border-[oklch(36%_0.04_145)] bg-[oklch(19%_0.022_155)] p-6 lg:sticky lg:top-8"
      aria-labelledby="create-heading"
    >
      <p className="m-0 text-xs uppercase tracking-[0.16em] text-[oklch(72%_0.09_145)]">
        Your space
      </p>
      <h2 id="create-heading" className="mt-1 text-2xl font-semibold">
        Create a room
      </h2>
      <label className="mt-5 block text-sm font-medium">
        Room name
        <input
          className="mt-2 w-full rounded-lg border border-[oklch(38%_0.025_155)] bg-[oklch(14%_0.015_155)] px-3 py-2.5"
          maxLength={80}
          onChange={(event) => onTitleChange(event.target.value)}
          placeholder="Design stand-up"
          value={title}
        />
      </label>
      <fieldset className="mt-5">
        <legend className="text-sm font-medium">Room lifetime</legend>
        <Choice
          checked={roomType === 'instant'}
          description="Deleted 10 minutes after everyone leaves."
          label="Instant"
          name="room-type"
          onChange={() => onRoomTypeChange('instant')}
        />
        <Choice
          checked={roomType === 'recurring'}
          description="Available until its owner closes it."
          label="Recurring"
          name="room-type"
          onChange={() => onRoomTypeChange('recurring')}
        />
      </fieldset>
      <fieldset className="mt-5">
        <legend className="text-sm font-medium">Access</legend>
        <Choice
          checked={visibility === 'public'}
          description="Anyone can join from the directory."
          label="Public"
          name="visibility"
          onChange={() => onVisibilityChange('public')}
        />
        <Choice
          checked={visibility === 'private'}
          description="Listed with a lock and protected by a six-letter password."
          label="Private"
          name="visibility"
          onChange={() => onVisibilityChange('private')}
        />
      </fieldset>
      <button
        className="mt-6 w-full rounded-lg bg-[oklch(78%_0.15_145)] px-4 py-3 font-semibold text-[oklch(18%_0.025_150)] disabled:cursor-not-allowed disabled:opacity-40"
        disabled={creating || title.trim() === ''}
        type="button"
        onClick={onCreate}
      >
        {creating ? 'Creating…' : 'Create and enter'}
      </button>
      {failure !== undefined && (
        <p className="mt-4 text-sm text-[oklch(84%_0.08_45)]" role="alert">
          {failure.message}
        </p>
      )}
    </section>
  );
}

function Choice({
  checked,
  description,
  label,
  name,
  onChange,
}: {
  checked: boolean;
  description: string;
  label: string;
  name: string;
  onChange: () => void;
}): React.JSX.Element {
  return (
    <label
      className={`mt-2 block cursor-pointer rounded-lg border p-3 ${checked ? 'border-[oklch(58%_0.11_145)] bg-[oklch(23%_0.035_145)]' : 'border-[oklch(32%_0.02_155)]'}`}
    >
      <span className="flex items-center gap-2">
        <input checked={checked} name={name} onChange={onChange} type="radio" />
        <strong className="text-sm">{label}</strong>
      </span>
      <span className="mt-1 block pl-6 text-xs leading-5 text-[oklch(66%_0.02_145)]">
        {description}
      </span>
    </label>
  );
}
